import { useEffect, useMemo, useRef } from 'react';

/**
 * HeartsBurst - всплеск красных сердечек над анкетой (супер-лайк).
 *
 * Анимация - Web Animations API с конкретными числовыми кейфреймами:
 * transform/opacity выполняются на композиторе GPU и не дёргаются,
 * даже когда главный поток занят перерисовкой React.
 *
 * @param {number} props.triggerKey - инкрементится при каждом супер-лайке (пересоздаёт сердечки)
 * @param {number} props.count - количество сердечек
 */
export function HeartsBurst({ triggerKey = 0, count = 18 }) {
  const containerRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- triggerKey нужен для пересоздания случайных траекторий при каждом супер-лайке
  const hearts = useMemo(
    () =>
      Array.from({ length: count }, () => ({
        left: 6 + Math.random() * 88,
        top: 16 + Math.random() * 62,
        size: 20 + Math.random() * 24,
        delay: Math.random() * 0.25,
        dx: Math.round((Math.random() - 0.5) * 320),
        dy: -Math.round(60 + Math.random() * 260),
        rot: Math.round((Math.random() - 0.5) * 200),
        duration: 1 + Math.random() * 0.6
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- triggerKey пересоздаёт случайные траектории при каждом супер-лайке
    [triggerKey, count]
  );

  useEffect(() => {
    if (!triggerKey) return;

    const container = containerRef.current;
    if (!container) return;

    const animations = [...container.querySelectorAll('svg')].map((heart, index) => {
      const { dx, dy, rot } = hearts[index];

      return heart.animate(
        [
          { opacity: 0, transform: 'translate(0px, 0px) rotate(0deg) scale(0)' },
          {
            opacity: 1,
            transform: `translate(${dx * 0.15}px, ${dy * 0.15}px) rotate(${rot * 0.2}deg) scale(1.3)`,
            offset: 0.18
          },
          {
            opacity: 1,
            transform: `translate(${dx * 0.55}px, ${dy * 0.55}px) rotate(${rot * 0.7}deg) scale(1)`,
            offset: 0.45
          },
          { opacity: 0.9, offset: 0.75 },
          { opacity: 0, transform: `translate(${dx}px, ${dy}px) rotate(${rot}deg) scale(0.7)` }
        ],
        {
          duration: hearts[index].duration * 1000,
          delay: hearts[index].delay * 1000,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'forwards'
        }
      );
    });

    return () => animations.forEach((animation) => animation.cancel());
  }, [triggerKey, hearts]);

  if (!triggerKey) return null;

  return (
    <div ref={containerRef} className="absolute inset-0 z-[60] pointer-events-none">
      {hearts.map((heart, index) => (
        <svg
          key={`${triggerKey}-${index}`}
          viewBox="0 0 24 24"
          style={{
            position: 'absolute',
            left: `${heart.left}%`,
            top: `${heart.top}%`,
            width: heart.size,
            height: heart.size,
            filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.45))',
            willChange: 'transform, opacity'
          }}
        >
          <path
            fill="#FF4D6D"
            d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
          />
        </svg>
      ))}
    </div>
  );
}
